<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar entidad pensionaria" : "Nueva entidad pensionaria" }}
      </h1>
    </div>

    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 760px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos de la entidad" : "Datos de la nueva entidad" }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">

                <!-- Nombre -->
                <div class="col-md-8">
                  <div class="form-group">
                    <label for="name">Nombre <span class="text-danger">*</span></label>
                    <input
                      id="name"
                      type="text"
                      v-model="form.name"
                      autocomplete="off"
                      placeholder="Nombre de la entidad"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.name.$error }"
                    />
                    <FormError
                      v-if="$v.form.name.$error && !$v.form.name.required"
                      message="El nombre es requerido"
                    />
                  </div>
                </div>

                <!-- Código -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="code">Código <span class="text-danger">*</span></label>
                    <input
                      id="code"
                      type="text"
                      v-model="form.code"
                      autocomplete="off"
                      placeholder="Ej: AFP01"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.code.$error }"
                      :disabled="editing"
                    />
                    <FormError
                      v-if="$v.form.code.$error && !$v.form.code.required"
                      message="El código es requerido"
                    />
                  </div>
                </div>

                <!-- Tipo -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Tipo <span class="text-danger">*</span></label>
                    <FormSelect
                      :items="typeOptions"
                      v-model="form.type"
                      defaultOption="Seleccione tipo"
                      :disabled="editing"
                      :class="{ 'custom-form-control-error': $v.form.type.$error }"
                    />
                    <FormError
                      v-if="$v.form.type.$error && !$v.form.type.required"
                      message="El tipo es requerido"
                    />
                  </div>
                </div>

                <!-- Aporte obligatorio -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="mandatoryRate">Aporte obligatorio <span class="text-danger">*</span></label>
                    <div class="input-group">
                      <input
                        id="mandatoryRate"
                        type="number"
                        v-model.number="form.mandatoryContributionRate"
                        step="0.0001"
                        min="0"
                        max="100"
                        placeholder="Ej: 10.00"
                        class="custom-form-control"
                        :class="{ 'custom-form-control-error': $v.form.mandatoryContributionRate.$error }"
                      />
                      <div class="input-group-append">
                        <span class="input-group-text">%</span>
                      </div>
                    </div>
                    <FormError
                      v-if="$v.form.mandatoryContributionRate.$error && !$v.form.mandatoryContributionRate.required"
                      message="El aporte obligatorio es requerido"
                    />
                    <FormError
                      v-if="$v.form.mandatoryContributionRate.$error && !$v.form.mandatoryContributionRate.between"
                      message="Debe ser un valor entre 0 y 100"
                    />
                  </div>
                </div>

                <!-- Campos solo para AFP -->
                <template v-if="form.type === 'AFP'">

                  <div class="col-md-4">
                    <div class="form-group">
                      <label for="insuranceRate">Prima de seguro <span class="text-danger">*</span></label>
                      <div class="input-group">
                        <input
                          id="insuranceRate"
                          type="number"
                          v-model.number="form.insurancePremiumRate"
                          step="0.0001"
                          min="0"
                          max="100"
                          placeholder="Ej: 1.74"
                          class="custom-form-control"
                          :class="{ 'custom-form-control-error': $v.form.insurancePremiumRate.$error }"
                        />
                        <div class="input-group-append">
                          <span class="input-group-text">%</span>
                        </div>
                      </div>
                      <FormError
                        v-if="$v.form.insurancePremiumRate.$error && !$v.form.insurancePremiumRate.required"
                        message="La prima de seguro es requerida para AFP"
                      />
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group">
                      <label for="flowRate">Comisión flujo <span class="text-danger">*</span></label>
                      <div class="input-group">
                        <input
                          id="flowRate"
                          type="number"
                          v-model.number="form.flowCommissionRate"
                          step="0.0001"
                          min="0"
                          max="100"
                          placeholder="Ej: 1.55"
                          class="custom-form-control"
                          :class="{ 'custom-form-control-error': $v.form.flowCommissionRate.$error }"
                        />
                        <div class="input-group-append">
                          <span class="input-group-text">%</span>
                        </div>
                      </div>
                      <FormError
                        v-if="$v.form.flowCommissionRate.$error && !$v.form.flowCommissionRate.required"
                        message="La comisión flujo es requerida para AFP"
                      />
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group">
                      <label for="mixedRate">Comisión mixta <span class="text-danger">*</span></label>
                      <div class="input-group">
                        <input
                          id="mixedRate"
                          type="number"
                          v-model.number="form.mixedCommissionRate"
                          step="0.0001"
                          min="0"
                          max="100"
                          placeholder="Ej: 0.87"
                          class="custom-form-control"
                          :class="{ 'custom-form-control-error': $v.form.mixedCommissionRate.$error }"
                        />
                        <div class="input-group-append">
                          <span class="input-group-text">%</span>
                        </div>
                      </div>
                      <FormError
                        v-if="$v.form.mixedCommissionRate.$error && !$v.form.mixedCommissionRate.required"
                        message="La comisión mixta es requerida para AFP"
                      />
                    </div>
                  </div>

                </template>

              </div>

              <div class="form-group-buttons text-right">
                <router-link :to="{ name: 'pension-entity-list' }" class="button button-light">
                  {{ editing ? "Volver" : "Cancelar" }}
                </router-link>
                <button class="button button-primary" type="submit">Guardar</button>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormError, FormSelect } from "wize-admin";
import { required, requiredIf, between } from "vuelidate/lib/validators";
import { PensionEntityService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

export default {
  components: { FormError, FormSelect },

  data() {
    return {
      editing: false,
      typeOptions: Object.values(LocalConstants.PensionEntityType).map((t) => ({
        key: t.key, label: t.label,
      })),
      form: {
        name:                     null,
        code:                     null,
        type:                     null,
        mandatoryContributionRate: null,
        insurancePremiumRate:      null,
        flowCommissionRate:        null,
        mixedCommissionRate:       null,
      },
    };
  },

  validations() {
    return {
      form: {
        name: { required },
        code: { required },
        type: { required },
        mandatoryContributionRate: {
          required,
          between: between(0, 100),
        },
        insurancePremiumRate: {
          required: requiredIf(() => this.form.type === "AFP"),
        },
        flowCommissionRate: {
          required: requiredIf(() => this.form.type === "AFP"),
        },
        mixedCommissionRate: {
          required: requiredIf(() => this.form.type === "AFP"),
        },
      },
    };
  },

  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    // Al cambiar tipo a ONP, limpiar campos AFP
    "form.type": function (val) {
      if (val !== "AFP") {
        this.form.insurancePremiumRate = null;
        this.form.flowCommissionRate   = null;
        this.form.mixedCommissionRate  = null;
      }
    },
  },

  methods: {
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);

        // Convertir porcentajes → decimales antes de enviar
        const data = {
          name: this.form.name,
          code: this.form.code,
          type: this.form.type,
          mandatoryContributionRate: this.form.mandatoryContributionRate != null
            ? this.form.mandatoryContributionRate / 100 : null,
          insurancePremiumRate: this.form.type === "AFP" && this.form.insurancePremiumRate != null
            ? this.form.insurancePremiumRate / 100 : null,
          flowCommissionRate: this.form.type === "AFP" && this.form.flowCommissionRate != null
            ? this.form.flowCommissionRate / 100 : null,
          mixedCommissionRate: this.form.type === "AFP" && this.form.mixedCommissionRate != null
            ? this.form.mixedCommissionRate / 100 : null,
        };

        let response;
        if (!this.editing) response = await PensionEntityService.save(data);
        else response = await PensionEntityService.update(this.$route.params.id, data);

        Alert.success("Se almacenó la información correctamente");
        if (!this.editing) {
          this.$router.push({
            name: "pension-entity-edit",
            params: { id: response.payload.id },
          });
          this.editing = true;
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al guardar");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },

    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const payload = (await PensionEntityService.get(id)).payload;

        // Convertir decimales → porcentajes para mostrar en inputs
        this.form = {
          name: payload.name,
          code: payload.code,
          type: payload.type,
          mandatoryContributionRate: payload.mandatoryContributionRate != null
            ? parseFloat((payload.mandatoryContributionRate * 100).toFixed(4)) : null,
          insurancePremiumRate: payload.insurancePremiumRate != null
            ? parseFloat((payload.insurancePremiumRate * 100).toFixed(4)) : null,
          flowCommissionRate: payload.flowCommissionRate != null
            ? parseFloat((payload.flowCommissionRate * 100).toFixed(4)) : null,
          mixedCommissionRate: payload.mixedCommissionRate != null
            ? parseFloat((payload.mixedCommissionRate * 100).toFixed(4)) : null,
        };
        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar la entidad");
        this.$router.push({ name: "pension-entity-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },

  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
